import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";
import Swal from "sweetalert2";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Utils } from "../../../../../shared/utils/Utils";
import {
  CriterioComportamientoSv,
  CriteriosComportamientoSvService,
} from "../../../../../shared/services/criterios-comportamiento-sv.service";
import { EvaluacionComportamientoSvService } from "../../../../../shared/services/evaluacion-comportamiento-sv.service";
import { PropietarioService } from "../../../../../shared/services/propietarios.service"; // ajusta al service real de colaboradores
import { PlanRefuerzoSeguimientoComponent } from "../../plan-refuerzo-seguimiento/plan-refuerzo-seguimiento/plan-refuerzo-seguimiento.component";

const UMBRAL_CUMPLE = 80;

@Component({
  selector: "app-evaluacion-comportamiento",
  templateUrl: "./evaluacion-comportamiento.component.html",
  styleUrls: ["./evaluacion-comportamiento.component.scss"],
})
export class EvaluacionComportamientoComponent implements OnInit {
  evaluacionForm!: FormGroup;
  activeTab = 1;

  guardando = false;
  cargandoListado = false;
  cargandoEdicion = false;
  cargandoCriterios = false;
  cargandoColaboradores = false;

  criterios: CriterioComportamientoSv[] = [];
  colaboradores: any[] = [];

  evaluaciones: any[] = [];
  evaluacionesFiltradas: any[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  filtroPeriodo: number | null = null;
  pageSize = 10;
  page = 1;

  modoEdicion = false;
  idEvaluacionEditando: number | null = null;

  resultadoGlobal = 0;
  cumpleGlobal = true;

  resumenColaborador: any = null;
  cargandoResumen = false;

  readonly Number = Number;
  readonly UMBRAL_CUMPLE = UMBRAL_CUMPLE;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private criteriosService: CriteriosComportamientoSvService,
    private evaluacionService: EvaluacionComportamientoSvService,
    private propietarioService: PropietarioService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarColaboradores();
    this.cargarCriterios();
    this.cargarEvaluaciones();
  }

  private inicializarFormulario(): void {
    this.evaluacionForm = this.fb.group({
      cedula_colaborador: [null, Validators.required],
      periodo: [new Date().getFullYear(), [Validators.required]],
      fecha_evaluacion: [null, Validators.required],
      evaluador: [null],
      observaciones_generales: [null],
      detalle: this.fb.array([]),
      plan_refuerzo: this.fb.array([]),
    });

    this.evaluacionForm.get("detalle")?.valueChanges.subscribe(() => this.calcularResultado());
    this.evaluacionForm.get("cedula_colaborador")?.valueChanges.subscribe(() => this.cargarResumenColaborador());
    this.evaluacionForm.get("periodo")?.valueChanges.subscribe(() => this.cargarResumenColaborador());
  }

  get detalleArray(): FormArray {
    return this.evaluacionForm.get("detalle") as FormArray;
  }

  get planRefuerzoArray(): FormArray {
    return this.evaluacionForm.get("plan_refuerzo") as FormArray;
  }

  /**
   * Comparador usado por los ng-select ligados a "cedula" (Colaborador, Evaluador, Responsable).
   * Es necesario porque el listado de colaboradores trae `cedula` como number, mientras que
   * `cedula_colaborador`/`evaluador`/`responsable` pueden llegar como string desde el backend
   * (getById, plan de refuerzo, etc.). Sin esto, ng-select compara por === estricta y no
   * marca ninguna opción como seleccionada al editar.
   */
  compararPorCedula = (a: any, b: any): boolean => {
    // ng-select a veces entrega el objeto completo del item (no solo el bindValue),
    // por lo que hay que extraer "cedula" cuando el valor recibido es un objeto.
    const valorA = a && typeof a === "object" ? a.cedula : a;
    const valorB = b && typeof b === "object" ? b.cedula : b;
    if (valorA === valorB) return true;
    if (valorA === null || valorA === undefined || valorB === null || valorB === undefined) return false;
    return String(valorA).trim() === String(valorB).trim();
  };

  // ============ CATÁLOGOS ============

  /**
   * Inyecta en `colaboradores` un registro sintético para una cédula que no
   * está en el listado (p. ej. porque ya no tiene plan de acción activo),
   * de modo que el ng-select pueda mostrar su nombre al editar.
   */
  private agregarColaboradorSiNoExiste(cedula: any, nombreCompleto: any): void {
    if (cedula === null || cedula === undefined) return;
    const cedulaStr = String(cedula).trim();
    const yaExiste = this.colaboradores.some((c) => String(c.cedula).trim() === cedulaStr);
    if (yaExiste) return;

    this.colaboradores = [
      ...this.colaboradores,
      { cedula: cedulaStr, nombreCompleto: nombreCompleto || cedulaStr },
    ];
  }

  private cargarColaboradores(): void {
    this.cargandoColaboradores = true;
    this.propietarioService
      .getPlanAccion()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        // cedula se normaliza a string aquí, una sola vez, para que coincida siempre
        // con el tipo que se usa al patchear el formulario (ver editarEvaluacion)
        this.colaboradores = this.normalizarLista(response).map((c: any) => ({
          ...c,
          cedula: c?.cedula != null ? String(c.cedula).trim() : c?.cedula,
        }));
        this.cargandoColaboradores = false;
      });
  }

  private cargarCriterios(): void {
    this.cargandoCriterios = true;
    this.criteriosService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.criterios = this.normalizarLista(response).filter((c) => Number(c.estado) === 1);
        this.construirDetalleArray();
        this.cargandoCriterios = false;
      });
  }

  private construirDetalleArray(detalleExistente: any[] = []): void {
    this.detalleArray.clear();

    this.criterios.forEach((criterio) => {
      const previo = detalleExistente.find(
        (d) => Number(d.id_criterio) === Number((criterio as any).id),
      );

      this.detalleArray.push(
        this.fb.group({
          id_criterio: [(criterio as any).id],
          criterio_nombre: [criterio.criterio],
          peso: [criterio.peso],
          cumple: [previo ? !!previo.cumple : false],
          calificacion: [previo?.calificacion ?? null],
          observacion: [previo?.observacion ?? null],
        }),
      );
    });

    this.calcularResultado();
  }

  // ============ CÁLCULO DE RESULTADO ============

  private calcularResultado(): void {
    const detalle = this.detalleArray.value as any[];
    if (!detalle.length) {
      this.resultadoGlobal = 0;
      this.cumpleGlobal = true;
      return;
    }

    const tienenPeso = detalle.some((d) => d.peso !== null && d.peso !== undefined);

    if (tienenPeso) {
      const pesoTotal = detalle.reduce((acc, d) => acc + Number(d.peso || 0), 0) || 1;
      const puntaje = detalle.reduce((acc, d) => acc + (d.cumple ? Number(d.peso || 0) : 0), 0);
      this.resultadoGlobal = Number(((puntaje / pesoTotal) * 100).toFixed(2));
    } else {
      const cumplidos = detalle.filter((d) => d.cumple).length;
      this.resultadoGlobal = Number(((cumplidos / detalle.length) * 100).toFixed(2));
    }

    this.cumpleGlobal = this.resultadoGlobal >= UMBRAL_CUMPLE;

    if (this.cumpleGlobal) {
      this.planRefuerzoArray.clear();
    }
  }

  // ============ PLAN DE REFUERZO (nuevas acciones, en creación/edición) ============

  agregarAccionRefuerzo(): void {
    this.planRefuerzoArray.push(
      this.fb.group({
        accion: ["", Validators.required],
        responsable: [null],
        fecha_compromiso: [null, Validators.required],
      }),
    );
  }

  eliminarAccionRefuerzo(index: number): void {
    this.planRefuerzoArray.removeAt(index);
  }

  // ============ LISTADO ============

  cargarEvaluaciones(): void {
    this.cargandoListado = true;
    this.evaluacionService.getAll().subscribe({
      next: (response: any) => {
        this.evaluaciones = this.normalizarLista(response);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.evaluaciones = [];
        this.evaluacionesFiltradas = [];
        this.cargandoListado = false;
      },
    });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "").toLowerCase().trim();

    let filtrados = this.evaluaciones.filter((item) => {
      const nombreColaborador =
        item?.ColaboradorData?.nombres || item?.ColaboradorData?.cedula || item?.cedula_colaborador || "";
      const coincideTexto = !termino || String(nombreColaborador).toLowerCase().includes(termino);
      const estadoItem = Number(item.estado);
      const coincideEstado = this.filtroEstado === null || estadoItem === this.filtroEstado;
      const coincidePeriodo = this.filtroPeriodo === null || Number(item.periodo) === this.filtroPeriodo;
      return coincideTexto && coincideEstado && coincidePeriodo;
    });

    filtrados = filtrados.sort((a, b) => (b.periodo || 0) - (a.periodo || 0));

    this.evaluacionesFiltradas = filtrados;
    this.page = 1;
  }

  // ============ GUARDAR ============

  guardar(): void {
    if (
      this.evaluacionForm.get("cedula_colaborador")?.invalid ||
      this.evaluacionForm.get("periodo")?.invalid ||
      this.evaluacionForm.get("fecha_evaluacion")?.invalid
    ) {
      this.evaluacionForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa colaborador, periodo y fecha de evaluación.", "warning");
      return;
    }

    if (this.detalleArray.length === 0) {
      Swal.fire("Sin criterios", "No hay criterios activos para evaluar.", "warning");
      return;
    }

    if (!this.cumpleGlobal && this.planRefuerzoArray.length === 0) {
      Swal.fire(
        "Plan de refuerzo requerido",
        "El resultado no alcanza el umbral de cumplimiento. Agrega al menos una acción de refuerzo.",
        "warning",
      );
      return;
    }

    const formValue = this.evaluacionForm.getRawValue();

    const payload = {
      cedula_colaborador: formValue.cedula_colaborador,
      periodo: Number(formValue.periodo),
      fecha_evaluacion: formValue.fecha_evaluacion,
      evaluador: formValue.evaluador,
      observaciones_generales: formValue.observaciones_generales,
      detalle: formValue.detalle.map((d: any) => ({
        id_criterio: d.id_criterio,
        cumple: !!d.cumple,
        calificacion: d.calificacion,
        observacion: d.observacion,
      })),
      plan_refuerzo: this.cumpleGlobal ? [] : formValue.plan_refuerzo,
    };

    this.guardando = true;

    const accion$ = this.modoEdicion
      ? this.evaluacionService.update(this.idEvaluacionEditando as number, payload)
      : this.evaluacionService.create(payload);

    accion$.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire(
          "Éxito",
          `Evaluación ${this.modoEdicion ? "actualizada" : "registrada"} correctamente.`,
          "success",
        );
        this.cancelarEdicion();
        this.cargarEvaluaciones();
        this.activeTab = 2;
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje = error?.error?.error || "No se pudo guardar la evaluación.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  // ============ EDITAR ============

  editarEvaluacion(evaluacion: any): void {
    if (Number(evaluacion?.estado) !== 1) {
      Swal.fire("Info", "Solo se puede editar cuando el registro está activo.", "info");
      return;
    }

    const id = Number(evaluacion?.id);
    this.cargandoEdicion = true;

    this.evaluacionService.getById(id).subscribe({
      next: (response: any) => {
        this.cargandoEdicion = false;
        const data = (response as any)?.data ?? response;
        if (!data) {
          Swal.fire("Error", "No se pudo cargar la evaluación.", "error");
          return;
        }

        this.modoEdicion = true;
        this.idEvaluacionEditando = id;

        // El colaborador/evaluador del registro puede no estar en el listado
        // de "colaboradores" (getPlanAccion), por lo que el ng-select no
        // tendría ninguna opción que coincida y se vería vacío al editar.
        this.agregarColaboradorSiNoExiste(data.cedula_colaborador, data.ColaboradorData?.nombreCompleto);
        this.agregarColaboradorSiNoExiste(data.evaluador, data.EvaluadorData?.nombreCompleto);

        this.evaluacionForm.patchValue({
          cedula_colaborador: data.cedula_colaborador,
          periodo: data.periodo,
          fecha_evaluacion: data.fecha_evaluacion,
          evaluador: data.evaluador,
          observaciones_generales: data.observaciones_generales,
        });

        const detalleActivo = (data.DetalleCriteriosData || []).filter(
          (d: any) => Number(d.estado) === 1,
        );
        this.construirDetalleArray(detalleActivo);

        // Las acciones ya creadas se gestionan en el modal de seguimiento,
        // aquí solo se permite AGREGAR nuevas si sigue sin cumplir
        this.planRefuerzoArray.clear();

        this.activeTab = 1;
      },
      error: (error: any) => {
        this.cargandoEdicion = false;
        const mensaje = error?.error?.error || "No se pudo cargar la evaluación para editar.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idEvaluacionEditando = null;
    this.evaluacionForm.reset({
      cedula_colaborador: null,
      periodo: new Date().getFullYear(),
      fecha_evaluacion: null,
      evaluador: null,
      observaciones_generales: null,
    });
    this.construirDetalleArray();
    this.planRefuerzoArray.clear();
  }

  // ============ CAMBIO DE ESTADO ============

  cambiarEstado(evaluacion: any, estado: number): void {
    const id = Number(evaluacion?.id);
    const accion = estado === 1 ? "activar" : "inactivar";

    Swal.fire({
      title: `¿Deseas ${accion} esta evaluación?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const accionServicio =
        estado === 1 ? this.evaluacionService.activar(id) : this.evaluacionService.inactivar(id);

      accionServicio.subscribe({
        next: () => {
          Swal.fire("Éxito", `Evaluación ${accion}da correctamente.`, "success");
          this.cargarEvaluaciones();
        },
        error: (error: any) => {
          const mensaje = error?.error?.error || `No se pudo ${accion} la evaluación.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  // ============ MODAL: SEGUIMIENTO PLAN DE REFUERZO ============

  verSeguimiento(evaluacion: any): void {
    const modalRef = this.modalService.open(PlanRefuerzoSeguimientoComponent, {
      backdrop: "static",
      size: "xl",
    });

    modalRef.componentInstance.idEvaluacion = Number(evaluacion.id);
    modalRef.componentInstance.nombreColaborador =
      evaluacion?.ColaboradorData?.nombres || evaluacion?.cedula_colaborador;

    modalRef.result.finally(() => {
      // Refresca por si se agregaron/actualizaron acciones desde el modal
      this.cargarEvaluaciones();
    });
  }

  // ============ EXPORTAR ============

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  cargarResumenColaborador(): void {
    const cedula = this.evaluacionForm.get("cedula_colaborador")?.value;
    const periodo = this.evaluacionForm.get("periodo")?.value;
    if (!cedula || !periodo) {
      this.resumenColaborador = null;
      return;
    }

    this.cargandoResumen = true;
    this.evaluacionService.getResumenColaborador(cedula, periodo).subscribe({
      next: (resumen: any) => {
        this.resumenColaborador = resumen;
        this.cargandoResumen = false;
      },
      error: () => {
        this.resumenColaborador = null;
        this.cargandoResumen = false;
      },
    });
  }
}